import { CartContext } from '@context/cart.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import sanitizer from '@helpers/sanitizer';
import React, { useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { Product } from '@interfaces/models/product';
import { useContextSelector } from 'use-context-selector';
import logger from '@helpers/utils/logger/client';
import clsx from 'clsx';
import styles from '../CTA-product.module.scss';

export interface CTANegotiationBlockProps {
  trackingFrom: string;
  product: Product;
  concurrentActiveNegotiations: number;
  isNegotiationAccepted: boolean;
  showBuyNow?: boolean;
  applyTopMargin?: boolean;
  setIsLoading?: (e) => void;
  onClose?: () => void;
}

const CTANegotiationBlock: React.FC<CTANegotiationBlockProps> = ({
  concurrentActiveNegotiations,
  trackingFrom,
  isNegotiationAccepted,
  showBuyNow,
  applyTopMargin,
  setIsLoading,
  onClose,
  product,
}) => {
  const addToShoppingCart = useContextSelector(CartContext, (v) => v.addToShoppingCart);
  const { sendAnalyticEvent: sendNegotiationAnalyticEvents } = useAnalyticEvents('mmao');
  const isProductIdInCart = useContextSelector(CartContext, (v) => v.isProductIdInCart);
  const productsInCart = useContextSelector(CartContext, (v) => v.productsInCart);
  const { t } = useTranslation();

  const addToCart = async (): Promise<void> => {
    try {
      onClose?.();
      await addToShoppingCart(product, false);
    } catch (e) {
      logger.error(e, 'Error adding to cart - MMAO-modal');
    }
  };

  const displayBuyNowButton = showBuyNow && !isProductIdInCart(productsInCart, product.id) && !isNegotiationAccepted;

  const handleBuyNow = async () => {
    // Track: This event gets triggered when the user clicks on the buy now in the negotiating area
    sendNegotiationAnalyticEvents('mmao_buy_now');
    // Add to cart when user clicks on `Buy Now` CTA
    try {
      setIsLoading(true);
      await addToCart();
    } catch (e) {
      setIsLoading(false);
      logger.error(e, 'Error adding to cart - CTA-buttons-block');
    }
  };

  /* TODO: Need to find better solution, instead of this ugly workaround :(
      - 'mmao_ongoing_banner_pdp' event is getting called before 'PageView' event
      - Due to this overlap this particular event is not getting logged on kibana
      - window?.tc_events_7 is still 'undefined' while this event is being sent to snowplow
  */
  // @ts-ignore
  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const tcEventLoaded = typeof window !== 'undefined' && typeof window?.tc_events_7 === 'function';

  useEffect(() => {
    // Track: When the negotiating area page is loaded with the banner either on MMAO popup or PDP
    if (tcEventLoaded) {
      sendNegotiationAnalyticEvents(trackingFrom, {
        eventValue: concurrentActiveNegotiations.toString(),
        eventProperty: product.id,
      });
    }
  }, [sendNegotiationAnalyticEvents, concurrentActiveNegotiations, trackingFrom, tcEventLoaded, product.id]);

  return (
    <>
      {
        <div className={clsx(styles.mmao__offers, applyTopMargin && styles['mmao__offers--topMargin'])}>
          <p className={styles.mmao__offers__text}>
            <span
              dangerouslySetInnerHTML={{
                __html: sanitizer(
                  concurrentActiveNegotiations > 1
                    ? t('MMAO.ONGOING_BANNER_PROGRESS_OFFER_PLURAL', {
                        offerCount: concurrentActiveNegotiations,
                      })
                    : t('MMAO.ONGOING_BANNER_PROGRESS_OFFER_SINGLE'),
                ),
              }}
            />
          </p>
          {displayBuyNowButton && (
            <div>
              <button
                className="vc-btn vc-btn--primary vc-btn--small"
                onClick={handleBuyNow}
              >
                {t('MMAO.ONGOING_BANNER_OFFER_BUY_NOW')}
              </button>
            </div>
          )}
        </div>
      }
    </>
  );
};

export default CTANegotiationBlock;
